// src/pages/Home.jsx
import { useEffect } from "react";
import { FaBolt, FaShieldAlt, FaUserCheck, FaMapMarkerAlt } from "react-icons/fa";
import Mock6 from "../assets/mock6.png";
import Mock1 from "../assets/mock1.png";
import Mock5 from "../assets/mock5.png";
import Aos from "aos";
import "aos/dist/aos.css";
import styles from "../ServicesMarquee.module.css";
import { categories } from "../utils/categories";
import Highlights1 from "../assets/Highlight1.jpg";
import Highlights2 from "../assets/Highlights2.jpg";
import Highlights3 from "../assets/Highlights3.jpeg";
import { NavLink } from "react-router-dom";

export default function Home() {
    useEffect(() => {
        Aos.init({ duration: 2000 });
    }, []);

    const scrollToTop = () => window.scrollTo(0, 0);

    const features = [
        {
            title: "Vetted Professionals",
            desc: "Every provider is background-checked and reviewed so you get reliable help every time.",
            image: require("../assets/VP.jpg"),
        },
        {
            title: "Instant Booking",
            desc: "Book trusted fixers in minutes and track job progress from the FixerFind mobile app.",
            image: require("../assets/IB.jpg"),
        },
        {
            title: "Transparent Pricing",
            desc: "Clear, upfront pricing and secure payments — no surprise costs after the job.",
            image: require("../assets/TP.jpg"),
        },
        {
            title: "Satisfaction Guarantee",
            desc: "We stand by our providers — if you’re not satisfied we resolve it quickly.",
            image: require("../assets/SG.jpg"),
        },
    ];

    const blogs = [
        {
            title: "Top 5 Home Service Challenges in Africa - And How FixerFind Solves Them",
            summary: "From Lagos to Nairobi, customers face similar challenges: uncertainty, delays, and lack of trust.",
            link: "https://medium.com/@fixerfind/top-5-home-service-challenges-in-africa-and-how-fixerfind-solves-them-starting-from-lagos-355bd850bc5b",
        },
        {
            title: "The Future of Home Services: From Informal Hustle to Digital Infrastructure",
            summary: "For decades, home services across Africa have operated in the shadows of the formal economy.",
            link: "https://medium.com/@fixerfind/the-future-of-home-services-from-informal-hustle-to-digital-infrastructure-82cddca1a6ed",
        },
        {
            title: "Why Trust Is Africa’s Biggest Infrastructure Gap",
            summary: "When people talk about infrastructure in Africa, they usually mean roads or power. Rarely do they mention trust.",
            link: "https://medium.com/@fixerfind/why-trust-is-africas-biggest-infrastructure-gap-6115e33bfad8",
        },
    ];

    const testimonials = [
        {
            name: "Amaka Udochi",
            role: "Short-Let Agent, Lekki Phase 1",
            text: "The app is a big help! Managing estate repairs became cheaper and easy. I can find my next hire on FixerFind in 3 mins",
        },
        {
            name: "Dayo Olamide",
            role: "Master of Ceremony, Surulere",
            text: "My 2 weeks of signup on FixerFind, I made face at an event which was hilarious and still had my apartment hose delivered & fixed under 1 hour.",
        },
        {
            name: "Elvis Sunday",
            role: "Plumber",
            text: "This is the new way for one to start their skill craft and gain value in it. Use my code to signup and thank me later",
        },
        {
            name: "Cynthia Ene",
            role: "FixerFind User, Lagos",
            text: "I want FixerFind to keep this up. There's discount, booking fee is free, plus the app is simple to understand.",
        }
    ];

    const reasons = [
        {
            icon: FaBolt,
            title: "Fast & Reliable Matching",
            desc: "Get the right professional in minutes, wherever you are.",
            color: "bg-blue-100 text-blue-600",
        },
        {
            icon: FaShieldAlt,
            title: "Safe & Secure",
            desc: "Protected payments and trusted professionals for peace of mind.",
            color: "bg-green-100 text-green-600",
        },
        {
            icon: FaUserCheck,
            title: "Verified Experts",
            desc: "Every service provider is verified for quality and trust.",
            color: "bg-purple-100 text-purple-600",
        },
        {
            icon: FaMapMarkerAlt,
            title: "Available Anywhere",
            desc: "Book services wherever you are — home, office, or on the go.",
            color: "bg-pink-100 text-pink-600",
        },
    ];

    const faqs = [
        { question: "Is FixerFind free to use?", answer: "Yes. Using the standard plan is free upon sign up. You only pay for services hired.", icon: "💸" },
        { question: "Are payments secured?", answer: "Yes, we use trusted fintech partners for safe transactions.", icon: "🔒" },
        { question: "Is FixerFind available outside Lagos?", answer: "No. FixerFind is currently only available in Lagos, Nigeria.", icon: "🌍" },
        { question: "Can I offer a service on FixerFind?", answer: "Yes — apply via the app and complete verification.", icon: "🛠️" },
        { question: "Does FixerFind offer 24/7 services?", answer: "Yes. Many categories are available on-demand, anytime.", icon: "⏰" },
    ];

    const handleDownload = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        if (/android/i.test(ua)) {
            window.open("https://play.google.com/store/apps/details?id=com.fixerfind.fixerfind&hl=en");
        } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
            window.open("https://apps.apple.com/app/fixerfind-hire-offer-jobs/id6504881996");
        } else {
            window.open("https://play.google.com/store/apps/details?id=com.fixerfind.fixerfind&hl=en");
        }
    };

    const primaryBtn =
        "inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-blue-600 font-semibold shadow-md hover:bg-blue-600 hover:text-white active:scale-95 transition";

    return (
        <div className="w-full">
            {/* HERO SECTION */}
            <section
                data-aos="fade-down"
                id="hero"
                className="relative bg-gradient-to-r from-blue-600 to-blue-400 text-white overflow-hidden py-24 px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center"
            >
                {/* Decorative Shapes */}
                <div className="absolute -top-16 -left-16 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>
                <div className="absolute -bottom-20 -right-16 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>

                {/* Text */}
                <div className="md:w-1/2 space-y-6 relative z-10">
                    <h1 className="text-5xl md:text-6xl font-extrabold leading-tight tracking-tight">
                        Save <span className="text-yellow-400 animate-pulse">Time & Money</span> on Services!
                    </h1>
                    <p className="mt-4 text-white/80 text-lg md:text-xl">
                        FixerFind connects you to skilled artisans, wellness & lifestyle services — simple booking, secure payments, and total peace of mind.
                    </p>

                    {/* CTA Button */}
                    <div className="mt-8">
                        <button
                            onClick={handleDownload}
                            className={`${primaryBtn} text-lg`}
                        >
                            Download FixerFind
                        </button>
                    </div>
                    <div className="mt-4 text-sm text-white/70">
                        Available on Android & iOS
                    </div>

                    {/* Feature Badges */}
                    <div className="mt-8 flex flex-wrap gap-3 text-sm">
                        <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full shadow">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2L15 8H9L12 2Z" />
                            </svg>
                            Verified Providers
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full shadow">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" />
                            </svg>
                            Secure Payments
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full shadow">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <rect width="18" height="10" x="3" y="7" rx="2" />
                            </svg>
                            Quick Response
                        </div>
                    </div>

                    <div className="mt-6 text-sm text-white/70">
                        Trusted by early adopters in Lagos — pilot results show 85%+ satisfaction rate.
                    </div>
                </div>

                {/* App Screenshots */}
                <div className="md:w-1/2 mt-10 md:mt-0 relative flex justify-center gap-6 z-10">
                    {/* Front image */}
                    <img
                        src={Mock1}
                        alt="FixerFind app preview 1"
                        className=" w-64 sm:w-56 md:w-60 rounded-3xl shadow-2xl transform hover:scale-105 transition duration-500 relative z-20" />

                    {/* Back / elevated image */}
                    <img
                        src={Mock6}
                        alt="FixerFind app preview 2"
                        className=" w-56 md:w-60 rounded-3xl shadow-2xl hidden md:block transform hover:scale-105 transition duration-500 md:-ml-28 md:-translate-y-14 md:z-10 lg:-ml-0 lg:-translate-y-8 lg:z-auto" />
                </div>
            </section>

            <div id="hero-end" />

            {/* HOW IT WORKS */}
            <section
                data-aos="fade-up"
                className="bg-gray-50 py-24 px-6 md:px-12 lg:px-24"
            >
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900">
                        Get Things Fixed in 3 Simple Steps
                    </h2>
                    <p className="mt-4 text-lg text-gray-600">
                        Save time, avoid stress, and hire trusted professionals with FixerFind.
                    </p>
                </div>

                {/* Steps */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-6xl mx-auto">

                    {/* Step 1 */}
                    <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold mb-6">
                            1
                        </div>
                        <h4 className="text-xl font-semibold mb-3">Search Services</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Find the exact service you need and request vetted provider near you.
                        </p>
                    </div>

                    {/* Step 2 */}
                    <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold mb-6">
                            2
                        </div>
                        <h4 className="text-xl font-semibold mb-3">Book & Pay Securely</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Schedule your job, pay safely online, and track progress in real time.
                        </p>
                    </div>

                    {/* Step 3 */}
                    <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 font-bold mb-6">
                            3
                        </div>
                        <h4 className="text-xl font-semibold mb-3">Relax & Review</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Get the job done, rate your provider, and access fast support 24/7.
                        </p>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section
                data-aos="fade-up"
                id="features"
                className="bg-gray-50 py-28 px-6 md:px-12 lg:px-24"
            >
                {/* Header */}
                <div className="max-w-4xl mx-auto text-center mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900">What We Offer</h2>
                    <p className="mt-2 text-gray-600">Services built around trust, speed, simplicity — and smart AI.</p>
                </div>

                {/* Feature Grid */}
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto">
                    {features.map((f, index) => (
                        <div
                            data-aos="fade-up"
                            data-aos-delay={index * 100}
                            key={f.title}
                            className="group relative bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300"
                        >
                            {/* Image / Icon */}
                            <div className="mb-8 flex justify-center">
                                <div className="flex items-center justify-center w-36 h-36 p-6 rounded-2xl bg-indigo-50 group-hover:scale-110 transition">
                                    <img
                                        src={f.image}
                                        alt={f.title}
                                        className="w-full h-full rounded-2xl"
                                    />
                                </div>
                            </div>

                            {/* Text */}
                            <h4 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                                {f.title}
                            </h4>
                            <p className="text-sm text-gray-600 leading-relaxed text-center">
                                {f.desc}
                            </p>

                            {/* Decorative Gradient */}
                            <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-indigo-200 to-transparent opacity-0 group-hover:opacity-100 transition" />
                        </div>
                    ))}
                </div>
            </section>

            {/* Why FixerFind */}

            <section className="relative py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-indigo-50 via-white to-purple-50 text-center">
                {/* Header */}
                <div className="max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                        Why Choose FixerFind?
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Trusted, fast, and reliable services that save you time and hassle.
                    </p>
                </div>

                {/* Feature Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {reasons.map((f, idx) => {
                        const Icon = f.icon;
                        return (
                            <div
                                key={idx}
                                className="p-8 rounded-3xl bg-white border border-white/30 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
                            >
                                <div
                                    className={`w-20 h-20 mx-auto flex items-center justify-center rounded-full mb-6 ${f.color} text-3xl`}
                                >
                                    <Icon />
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                                    {f.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">{f.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>


            <section className="relative py-32 overflow-hidden bg-gradient-to-br from-indigo-50 via-slate-50 to-purple-50">
                <div className="max-w-4xl mx-auto text-center px-6 mb-20">
                    <h2 className="text-4xl font-bold">Explore Our Services</h2>
                    <p className="mt-4 text-gray-600">
                        A curated marketplace of trusted professionals.
                    </p>
                </div>

                <div className="relative">
                    <div className={styles.marquee}>
                        {[...categories, ...categories].map((cat, i) => (
                            <div
                                key={`${cat.id}-${i}`}
                                className="min-w-[280px] rounded-2xl p-6 bg-white border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"                            >
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                                    style={{ backgroundColor: `${cat.color}22` }}
                                >
                                    {(() => {
                                        const Icon = cat.icon;
                                        <Icon size={28} style={{ color: cat.color }} />
                                        return Icon ? (
                                            <Icon size={28} style={{ color: cat.color }} />
                                        ) : null;
                                    })()}
                                </div>
                                <h4 className="font-semibold">{cat.label}</h4>
                                <p className="text-sm text-gray-600">{cat.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Highlights Section */}
            <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-24">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                    Our Kind Of Users
                </h2>

                <div className="grid gap-12 md:grid-cols-3">
                    {[
                        {
                            title: "Customer",
                            type: "image",
                            src: Highlights1,
                            // src: require("../assets/HighlightsVid1.mp4"),
                        },
                        {
                            title: "Artisan/Provider",
                            type: "image",
                            src: Highlights2,
                            // src: require("../assets/HighlightsVid2.mp4"),
                        },
                        {
                            title: "Store Owner",
                            type: "image",
                            src: Highlights3,
                            // src: require("../assets/HighlightsVid3.mp4"),
                        },
                    ].map((item, i) => (
                        <div
                            data-aos="fade-up"
                            key={i}
                            className="relative group overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-transform transform hover:scale-105"
                        >
                            {/* MEDIA */}
                            {item.type === "image" ? (
                                <img
                                    src={item.src}
                                    alt={item.title}
                                    className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                            ) : (
                                <video
                                    src={item.src}
                                    className="w-full h-72 object-cover rounded-3xl"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            )}

                            {/* OVERLAY */}
                            <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6 pointer-events-none">
                                <h3 className="text-xl font-bold text-blue-600">{item.title}</h3>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Testimonials */}
            <section
                data-aos="fade-up"
                id="testimonials"
                className="relative py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-indigo-50 via-white to-purple-50 text-center"
            >
                {/* Header */}
                <div className="max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                        What Users Say
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Real experiences from our satisfied users in Lagos.
                    </p>
                </div>

                {/* Testimonials Grid */}
                <div className="grid sm:grid-cols-2 gap-8">
                    {testimonials.map((t, idx) => (
                        <div
                            key={idx}
                            className="relative p-8 rounded-3xl bg-white border border-white/30 shadow-lg hover:shadow-2xl transition-transform transform hover:-translate-y-2"
                        >
                            {/* Quote Icon */}
                            <div className="absolute -top-5 left-5 text-indigo-500 text-5xl">“</div>

                            {/* Text */}
                            <p className="text-gray-700 mb-6 leading-relaxed">{t.text}</p>

                            {/* Footer with Avatar */}
                            <div className="flex items-center justify-center gap-4">
                                <img
                                    src={t.avatar || `https://ui-avatars.com/api/?name=${t.name}&background=random&rounded=true`}
                                    alt={t.name}
                                    className="w-12 h-12 rounded-full object-cover"
                                />
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">{t.name}</p>
                                    <p className="text-gray-500 text-sm">{t.role}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* App Preview Video */}
            <section className="relative py-28 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-indigo-50 via-white to-pink-50 overflow-hidden">
                <div className="absolute -top-20 -left-20 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse"></div>
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>

                <div className="relative max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
                    <div data-aos="fade-up" className="md:w-1/2 flex justify-center">
                        <div className="relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
                            <iframe
                                width="100%"
                                height="315"
                                src="https://www.youtube.com/embed/CbZ3v_1N4pI"
                                title="FixerFind Intro"
                                className="rounded-3xl"
                                allowFullScreen
                            ></iframe>
                            <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold shadow">Demo</div>
                        </div>
                    </div>

                    <div data-aos="fade-up" className="md:w-1/2 space-y-6">
                        <h2 className="text-4xl font-extrabold text-gray-900">Experience Seamless Service</h2>
                        <p className="text-gray-600 text-lg">
                            From booking to payment, FixerFind makes everything simple, transparent, and reliable. Manage your services in one app.
                        </p>

                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                            <li>Quick & trusted bookings</li>
                            <li>Secure payments & updates</li>
                            <li>Verified professionals</li>
                        </ul>

                        <button
                            onClick={handleDownload}
                            className="mt-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:scale-105 hover:shadow-2xl transition-transform duration-300"
                        >
                            Download Now
                        </button>
                    </div>
                </div>
            </section>

            {/* Blog Preview */}
            <section className="relative py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-indigo-50 via-white to-purple-50 text-center">
                {/* Header */}
                <div className="max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                        From Our Blog
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Insights, tips, and stories from the world of FixerFind and home services.
                    </p>
                </div>

                {/* Blog Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {blogs.map((post, idx) => (
                        <a
                            key={idx}
                            href={post.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-8 rounded-3xl bg-white border border-white/30 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 group"
                        >
                            <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-indigo-600 transition">
                                {post.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-6">{post.summary}</p>
                            <span className="inline-block px-5 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 transition">
                                Read More →
                            </span>
                        </a>
                    ))}
                </div>
            </section>

            <section
                data-aos="fade-up"
                className="relative py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 overflow-hidden"
            >
                {/* Decorative Shapes */}
                <div className="absolute -top-16 -left-16 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse"></div>
                <div className="absolute -bottom-16 -right-16 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>

                <div className="relative max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
                    {/* Text Content */}
                    <div className="flex-1 text-center md:text-left">
                        <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900">
                            Bring Your Skill Onboard!
                        </h2>
                        <p className="text-gray-600 mb-6 text-lg">
                            Offer your expertise, reach more customers, and earn effortlessly.
                            Get 4–6 client payments in your pocket every month without extra costs.
                        </p>
                        <button
                            onClick={handleDownload}
                            className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:scale-105 hover:shadow-2xl transition-transform duration-300">
                            Earn <span>&#8358;</span>150,000 Monthly →
                        </button>
                    </div>

                    {/* Image / Illustration */}
                    <div className="flex-1">
                        <img
                            src={Mock5}
                            alt="Career Illustration"
                            className="rounded-2xl shadow-xl mx-auto md:mx-0 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg" />
                    </div>
                </div>
            </section>

            {/* FAQ Mini */}
            <section
                data-aos="fade-up"
                className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-white via-indigo-50 to-purple-50"
            >
                <div className="max-w-4xl mx-auto text-center mb-16">
                    <h2 className="text-4xl font-extrabold mb-4 text-gray-900">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Answers to the most common queries about FixerFind and how we make services seamless.
                    </p>
                </div>

                <div className="grid gap-6 max-w-4xl mx-auto">
                    {faqs.map((faq, idx) => (
                        <div
                            key={idx}
                            className="group relative p-6 bg-white rounded-3xl shadow-lg cursor-pointer overflow-hidden"
                        >
                            <summary className="flex items-center justify-between font-semibold text-gray-900 list-none">
                                <span className="flex items-center gap-3">
                                    <span className="text-blue-600 text-2xl">{faq.icon}</span>
                                    {faq.question}
                                </span>
                                <span className="transition-transform duration-300 group-open:rotate-45 text-blue-600">
                                    +
                                </span>
                            </summary>
                            <p className="mt-3 text-gray-600 leading-relaxed transition-all duration-300">
                                {faq.answer}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact / CTA */}
            <section data-aos="fade-up" className="relative py-28 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-purple-50 via-indigo-50 to-white overflow-hidden">
                {/* Decorative Circles */}
                <div className="absolute -top-16 -left-16 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>
                <div className="absolute -bottom-20 -right-16 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>

                <div className="relative max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="flex-1 text-center md:text-left space-y-3">
                        <h3 className="text-3xl font-extrabold text-gray-900">Want the Good Life Made Easy?</h3>
                        <p className="text-gray-600 text-lg">
                            Join thousands of users who book, pay, and manage services seamlessly with FixerFind.
                        </p>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4">
                        <button
                            onClick={handleDownload}
                            className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-semibold shadow-xl hover:scale-105 hover:shadow-2xl transition-transform duration-300"
                        >
                            Download FixerFind
                        </button>
                        <NavLink
                            to="/premium"
                            onClick={scrollToTop}
                            className="px-6 py-4 border-2 border-gray-300 text-gray-900 rounded-2xl font-semibold hover:bg-gray-100 transition"
                        >
                            Upgrade to Premium
                        </NavLink>
                    </div>
                </div>
            </section>
        </div>
    );
}
